/**
 * Admin Overview date-range resolver. Isolated + pure so the timezone-boundary
 * logic is unit-testable on its own: preset windows compute in the browser's
 * LOCAL time (e.g. "Today" = local midnight → now) and serialize to ISO-8601
 * UTC, matching what datetimeLocal.ts does for the Activity tab's inputs.
 *
 * Presets recompute their window at call time — callers must persist the preset
 * KEY (not its absolute timestamps) so a tab left open overnight, or a shared
 * link, still resolves to "today" on load rather than the day it was opened.
 */
import { localInputToIso } from './datetimeLocal';

export const RANGE_PRESETS = ['all', 'today', '7d', '30d', 'custom'] as const;
export type RangePreset = (typeof RANGE_PRESETS)[number];

export const PRESET_LABELS: Record<RangePreset, string> = {
  all: 'All time',
  today: 'Today',
  '7d': 'Last 7 days',
  '30d': 'Last month',
  custom: 'Custom',
};

export interface DateRange {
  fromDate?: string; // ISO-8601 UTC
  toDate?: string; // ISO-8601 UTC
}

export function isRangePreset(v: string | null): v is RangePreset {
  return v != null && (RANGE_PRESETS as readonly string[]).includes(v);
}

/**
 * Resolve a fixed preset to an ISO-UTC window. 'all' carries no bounds (the
 * request goes out param-less); 'custom' is not a fixed window and resolves to
 * empty here — resolve it from the inputs via resolveRange().
 */
export function resolvePreset(preset: RangePreset, now: Date = new Date()): DateRange {
  switch (preset) {
    case 'today': {
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      return { fromDate: start.toISOString(), toDate: now.toISOString() };
    }
    case '7d':
      return {
        fromDate: new Date(now.getTime() - 7 * 86_400_000).toISOString(),
        toDate: now.toISOString(),
      };
    case '30d':
      // Rolling 30 days, for consistency with "Last 7 days" (not the previous
      // calendar month).
      return {
        fromDate: new Date(now.getTime() - 30 * 86_400_000).toISOString(),
        toDate: now.toISOString(),
      };
    case 'all':
    case 'custom':
      return {};
  }
}

/**
 * Effective ISO-UTC range for any selection. Presets re-resolve against `now`;
 * 'custom' converts the two datetime-local input strings (local → ISO-UTC).
 */
export function resolveRange(
  preset: RangePreset,
  customFrom: string,
  customTo: string,
  now: Date = new Date(),
): DateRange {
  if (preset === 'custom') {
    return {
      fromDate: localInputToIso(customFrom) ?? undefined,
      toDate: localInputToIso(customTo) ?? undefined,
    };
  }
  return resolvePreset(preset, now);
}

/**
 * A range the email card can honor needs BOTH bounds — its CloudWatch pull
 * takes an explicit start + end, so a one-sided range can't drive it.
 */
export function isBoundedRange(r: DateRange): boolean {
  return Boolean(r.fromDate && r.toDate);
}
